import React from "react";
import { motion } from "framer-motion";
import { STUDENT_PHOTOS, LOGOS } from "../data/content";
import { useBooking } from "./BookingModal";

const EASE = [0.7, 0, 0.2, 1];

const StudentsSection = () => {
  const { openBooking } = useBooking();
  return (
    <section
      id="teaching"
      data-testid="students-section"
      className="relative overflow-hidden border-t border-[color:var(--border)] bg-[color:var(--surface)] px-6 py-32 md:px-12 md:py-48"
    >
      {/* oversized background numeral */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 0.06 }}
        viewport={{ once: true }}
        transition={{ duration: 1.5 }}
        className="pointer-events-none absolute -left-10 top-16 select-none font-serif text-[42vw] leading-none text-[color:var(--primary)] md:-left-8 md:text-[22vw]"
      >
        06
      </motion.div>

      <div className="relative mx-auto max-w-[1400px]">
        {/* Header */}
        <div className="mb-16 grid grid-cols-1 items-end gap-8 md:mb-24 md:grid-cols-12">
          <div className="md:col-span-8">
            <div className="chip mb-6">
              <span className="dot" />
              06 · Inside the Academy
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, ease: EASE }}
              className="font-serif text-5xl leading-[0.9] tracking-tight md:text-7xl lg:text-8xl"
            >
              How students
              <br />
              <span className="italic-serif text-[color:var(--primary)]">
                are taught
              </span>{" "}
              at 99.
            </motion.h2>
          </div>
          <div className="md:col-span-4 md:justify-self-end">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="flex items-center gap-4"
            >
              <img
                src={LOGOS.iao}
                alt="IAO"
                className="h-14 w-14 rounded-full object-cover md:h-16 md:w-16"
              />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[color:var(--muted)]">
                  Curriculum by
                </div>
                <div className="mt-1 font-serif text-lg leading-tight">
                  IAO-accredited
                  <br />
                  <span className="italic-serif text-[color:var(--primary)]">
                    programme
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* 3-column magazine grid */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-6">
          {STUDENT_PHOTOS.map((s, i) => {
            // Editorial magazine layout — middle card taller, offsets on desktop
            const span =
              i === 0
                ? "md:col-span-4 md:pt-16"
                : i === 1
                  ? "md:col-span-4"
                  : "md:col-span-4 md:pt-24";
            return (
              <motion.figure
                key={i}
                data-testid={`student-photo-${i}`}
                initial={{ opacity: 0, y: 60, clipPath: "inset(10% 10% 10% 10%)" }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                  clipPath: "inset(0% 0% 0% 0%)",
                }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 1.1,
                  delay: i * 0.12,
                  ease: EASE,
                }}
                className={`group relative ${span}`}
              >
                <div className="relative aspect-[3/4] overflow-hidden bg-black">
                  <img
                    src={s.src}
                    alt={s.title}
                    className="h-full w-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.7,0,0.2,1)] group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  {/* numeric marker */}
                  <div className="absolute left-4 top-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-white/80">
                    <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--primary)]" />
                    0{i + 1} / 03
                  </div>
                </div>
                {/* caption block */}
                <figcaption className="mt-5 flex items-start gap-4">
                  <div className="mt-2 h-px w-8 shrink-0 bg-[color:var(--primary)]" />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[color:var(--primary)]">
                      {s.kicker}
                    </div>
                    <h3 className="mt-2 font-serif text-2xl leading-tight md:text-3xl">
                      {s.title}
                    </h3>
                    <p className="mt-3 max-w-xs text-sm leading-relaxed text-[color:var(--muted)]">
                      {s.caption}
                    </p>
                  </div>
                </figcaption>
              </motion.figure>
            );
          })}
        </div>

        {/* Bottom CTA row */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-20 flex flex-col items-start gap-6 border-t border-[color:var(--border)] pt-10 md:flex-row md:items-center md:justify-between"
        >
          <p className="max-w-lg text-sm leading-relaxed text-[color:var(--muted)] md:text-base">
            Theory in the mornings, live client work in the afternoons —
            every batch trains inside a working salon, on real hair, real
            skin, real chairs. Certificates are IAO-endorsed.
          </p>
          <button
            data-testid="students-enrol-btn"
            onClick={openBooking}
            className="inline-flex items-center gap-3 border border-[color:var(--primary)] bg-[color:var(--primary)]/10 px-6 py-4 font-mono text-[10px] uppercase tracking-[0.3em] transition hover:bg-[color:var(--primary)]"
          >
            Join the next batch →
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default StudentsSection;
